import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import Highlighter from 'react-highlight-words';
import { textColor, THEME_PRIMARY, THEME_SILVER, themeColor } from '../../styles/basics/colors';
import { Input } from './Input';

const StInputSuggestionContainer = styled.div`
    position: relative;
`;

StInputSuggestionContainer.displayName = 'InputWithSuggestions__StInputSuggestionContainer';

const StSuggestions = styled.ul`
    position: absolute;
    top: 32px;
    padding: 5px;
    margin: 0;
    min-width: 150px;
    background-color: white;
    list-style: none;
    border: 1px solid ${themeColor(THEME_SILVER)};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    max-height: 150px;
    overflow: auto;
`;

const StSuggestionItem = styled.li`
    padding: 2px 5px;
    white-space: nowrap;
    line-height: 24px;
    cursor: pointer;

    :hover {
        ${textColor(THEME_PRIMARY)};
    }
`;

const StPlus = styled.i`
    font-size: 14px;
    margin-right: 5px;
    ${textColor(THEME_PRIMARY)};
`;

function buildSuggestion(onSuggestionClickFunc, suggestion, inputValue) {
    return (
        <StSuggestionItem onClick={() => onSuggestionClickFunc(suggestion)} key={`suggestion-item-${suggestion}`}>
            <StPlus className="strong">+&nbsp;</StPlus>
            <Highlighter
                key={`suggestion-${suggestion}`}
                autoEscape={true}
                highlightTag="span"
                highlightStyle={{ fontWeight: 'bold' }}
                searchWords={Array.of(inputValue)}
                textToHighlight={suggestion}
            />
        </StSuggestionItem>
    );
}

export const InputWithSuggestions = forwardRef<
    HTMLInputElement,
    {
        suggestions: string[];
        onSuggestionClick?: (element: string) => void;
        onChange?: (value: string) => void;
        excludedSuggestions?: string[];
        suffixIcon?: string;
        prefixIcon?: string;
        iconColor?: string;
        onEnterKey?: (e: string) => void;
        onEscapeKey?: (e: string) => void;
        onSuffixIconClick?: (value: string) => void;
        loading?: boolean;
    }
>((props, ref) => {
    const { suggestions, onSuggestionClick, onChange, excludedSuggestions = [] } = props;
    const [inputValue, setInputValue] = useState('');

    const onSuggestionClickFunc = onSuggestionClick ? suggestion => onSuggestionClick(suggestion) : null;

    if (isNil(suggestions) || !isArray(suggestions)) {
        return null;
    }

    const onChangeFunc = (value: string) => {
        if (onChange) onChange(value);
        setInputValue(value);
    };

    const remainingSuggestions = suggestions.filter(suggestion => !excludedSuggestions.includes(suggestion));

    const suggestionElement =
        suggestions.length > 0 && remainingSuggestions.length > 0 ? (
            <StSuggestions data-testid="styled-suggestions">
                {suggestions
                    .filter(suggestion => !excludedSuggestions.includes(suggestion))
                    .map(suggestion => buildSuggestion(onSuggestionClickFunc, suggestion, inputValue))}
            </StSuggestions>
        ) : null;

    return (
        <StInputSuggestionContainer ref={ref}>
            <Input {...props} onChange={onChangeFunc} focusOnCreate={true} />
            {suggestionElement}
        </StInputSuggestionContainer>
    );
});
